import {
  Sstv2MigrationPhase,
  Sstv2MigrationProcessingState,
} from "shared/sstv2MigrationData"
import { Sstv2Migration } from "shared/sstv2Migration"
import { describePhase, describeProcessingState } from "./sstv2Vocabulary"

/**
 * A migration row joined with the human-readable names resolved from the
 * SaGetTenants / SaGetLockers access patterns.
 *
 * ONE ROW PER MIGRATION JOB — i.e. per ATTEMPT, keyed by `migrationId` (the
 * partner's `sl2_locker_id` for that run). A locker that was migrated, rolled
 * back and migrated again has two rows with independent phases, counters and
 * timelines. Until 2026-07-28 there was one row per LOCKER, which merged every
 * attempt into a single self-contradictory row.
 */
export type Sstv2MigrationRow = {
  id: string // Sstv2Migration Thing id
  tenantId: string
  tenantName: string
  /**
   * The JOB id — this attempt's `sl2_locker_id`. Also the V2 locker's SST id.
   *
   * OPTIONAL on purpose: a record written by a pre-2026-07-28 consumer has no
   * `migrationId` (it was keyed by locker). Those rows are uninterpretable as jobs
   * but must still render — a single legacy row taking the whole grid down is
   * worse than showing it as legacy. Every consumer of this field must guard.
   */
  migrationId?: string
  sl1LockerId: string
  v1LockerName: string
  v2LockerName: string
  /** How many attempts this locker has had in total (for the "attempt N of M" hint). */
  attemptCount: number
  /** 1-based index of this attempt, oldest first. */
  attemptIndex: number
  /** When this attempt's first event landed — the row's own start time. */
  startedAt: string
  phase: Sstv2MigrationPhase | "NOT_STARTED"
  isRollback: boolean
  lastEventType: string
  lastEventAt: string
  createdSl2LockerThingId?: string
  /**
   * Migration JOB state (DEV-193, scale redesign §4.3/§6). Distinct from the
   * partner-derived `phase`: `processingState` is OUR pipeline's state machine,
   * and the split active/history counters drive the two live progress bars in
   * the detail drawer. `activeDiscrepancy` is surfaced as an advisory banner.
   */
  processingState?: Sstv2MigrationProcessingState
  activeReceived?: number
  /** Reservations FLIPPED IN PLACE — the V1 row rewritten to V2 identity (D2). */
  activeProcessed?: number
  activeFailed?: number
  /**
   * ADOPTED: a brand-new Thing created from the partner snapshot alone, because
   * SL2 had no V1 ancestor to flip (D10). The only case in which migration still
   * creates a Reservation Thing — everything else is an in-place rewrite.
   */
  activeAdopted?: number
  /**
   * The HISTORY stream — since D37 (2026-07-31) these mean exactly what their
   * `active*` twins mean, because history gets the identical treatment.
   *
   * ⚠ `historyProcessed` in particular changed meaning under `d9047759b`. It used to
   * be credited by INGESTION (under D5 the mapping write was the processing), so the
   * bar filled as the partner sent batches. It is now written only by the flip's own
   * walk, which runs AFTER the active walk — so this stream can sit at 0 for a long
   * while after the locker is already live on V2, and that is correct.
   */
  historyReceived?: number
  historyProcessed?: number
  historyFailed?: number
  historyAdopted?: number
  /**
   * FOLD PROVENANCE per stream (2026-07-31). How many mappings a COMPLETED walk of
   * that partition established its numerators against — the only thing that can tell a
   * genuinely finished stream from one whose counters merely balance.
   *
   * ABSENT on every attempt that ran before the stamp existed, which is why the UI
   * must treat "no stamp" as UNPROVEN rather than as a rendering gap: an attempt like
   * `ace1c2c7…` balances perfectly and was never walked.
   */
  activeFoldedCount?: number
  historyFoldedCount?: number
  /** Partner-reported failure_list count; excluded from stream completion. */
  partnerFailed?: number
  operationalEventReceived?: boolean
  activeDiscrepancy?: boolean
  /** A stream finished with failures — `COMPLETE` alone would read as clean. */
  completedWithFailures?: boolean
  /**
   * SEVERE: the active stream finished having flipped NOTHING, so every occupied
   * door on the V2 locker renders unlinked ("unavailable") to operators.
   *
   * The field NAME is the backend entity's and is deliberately not renamed here —
   * it predates the in-place redesign. What it means today is "nothing was
   * flipped and nothing was adopted".
   */
  activeMaterializationEmpty?: boolean
  /** the full source entity, for the detail drawer timeline */
  source: Sstv2Migration
}

/** MUI Chip colors per phase. Rollback phases are red ("error"); no yellow. */
export const phaseChipColor = (
  phase: Sstv2MigrationRow["phase"],
): "default" | "info" | "primary" | "success" | "error" => {
  switch (phase) {
    case "ROLLED_BACK":
    case "ROLLBACK_STARTED":
      return "error"
    case "COMPLETED":
      return "success"
    case "OPERATIONAL":
      return "primary"
    case "ACTIVE_MIGRATED":
      return "info"
    default:
      return "default"
  }
}

export const isRollbackPhase = (phase: Sstv2MigrationRow["phase"]): boolean =>
  phase === "ROLLED_BACK" || phase === "ROLLBACK_STARTED"

/**
 * True only for the rollback LIFECYCLE events (`migration:rollback:started`
 * / `migration:rollback:done`) — used to colour the timeline. Must NOT match
 * the history-step batch event `migration:rollback:reservations:done`, which
 * despite the `rollback` substring is a normal history-migration event, not a
 * rollback. A naive `event.includes("rollback")` mis-paints it red.
 */
export const isRollbackLifecycleEvent = (event: string): boolean =>
  event === "migration:rollback:started" ||
  event === "migration:rollback:done"

/**
 * i18n key for a phase label.
 *
 * Delegates to `./sstv2Vocabulary`, which is the single table pairing every rendered
 * token with its plain-English gloss. Prefer `describePhase` + `Sstv2TermChip` at new
 * call sites: this helper returns a key only, so it cannot carry the gloss and it
 * cannot show the raw value of a phase this build does not model.
 */
export const phaseLabelKey = (phase: Sstv2MigrationRow["phase"]): string =>
  describePhase(phase).labelKey

/**
 * The header numbers. Deliberately five, all actionable, all about JOBS except
 * `lockersLive`:
 *  - `jobs` / `lockers` — how many attempts across how many distinct lockers;
 *  - `inFlight` — attempts that are neither COMPLETED nor rolled back;
 *  - `completed` / `rolledBack` — terminal outcomes;
 *  - `stalled` — in-flight attempts past their phase's stall threshold (the only
 *    number that means "go look at something now");
 *  - `lockersLive` — lockers whose LATEST attempt is OPERATIONAL-or-later, i.e.
 *    actually serving on V2.
 *
 * The previous version reported `cutoverPct` / `completedPct` over the record
 * count. Once a record is one ATTEMPT rather than one locker, those ratios are
 * uninterpretable (a locker migrated 20 times drags them down), so they are gone —
 * fleet-level V1-vs-V2 share is the rollup section's job.
 */
export type Sstv2MigrationMetricCounts = {
  jobs: number
  lockers: number
  inFlight: number
  completed: number
  rolledBack: number
  stalled: number
  lockersLive: number
}

// ---------------------------------------------------------------------------
// Stall detection (Item 4)
// ---------------------------------------------------------------------------

/**
 * Per-phase "expected next event within N minutes" thresholds. Past this, a
 * pre-terminal migration is flagged Stalled. As a passive observer the ONLY
 * stall signal we have is elapsed-since-last-event (we don't drive the flow).
 * Terminal phases (COMPLETED / ROLLED_BACK) are never stalled — omitted here.
 */
export const STALL_THRESHOLD_MINUTES: Partial<
  Record<Sstv2MigrationRow["phase"], number>
> = {
  ACTIVE_MIGRATED: 30, // migrated → operational (cutover) expected within 30m
  OPERATIONAL: 60, // operational → completed (history) expected within 60m
  ROLLBACK_STARTED: 30, // rollback:started → rollback:done expected within 30m
}

/** Parse a partner naive-UTC timestamp (no trailing Z) as a UTC epoch (ms). */
export const parseNaiveUtcMs = (ts?: string | null): number => {
  if (!ts) return NaN
  const z = /[zZ]|[+-]\d\d:?\d\d$/.test(ts) ? ts : `${ts}Z`
  return Date.parse(z)
}

export type StallState = {
  minutesSinceLastEvent: number
  isStalled: boolean
  /** true when the phase is terminal (stall is not applicable). */
  terminal: boolean
}

/**
 * Compute stall state for a row given a reference `nowMs`. Pure + deterministic
 * (the caller supplies `nowMs`) so it is trivially testable and re-renders
 * cleanly under polling.
 */
export const computeStall = (
  phase: Sstv2MigrationRow["phase"],
  lastEventAt: string,
  nowMs: number,
): StallState => {
  const lastMs = parseNaiveUtcMs(lastEventAt)
  const minutesSinceLastEvent = Number.isFinite(lastMs)
    ? Math.max(0, Math.floor((nowMs - lastMs) / 60000))
    : 0
  const threshold = STALL_THRESHOLD_MINUTES[phase]
  const terminal = threshold === undefined
  return {
    minutesSinceLastEvent,
    terminal,
    isStalled: !terminal && minutesSinceLastEvent > (threshold as number),
  }
}

/** Human-readable "3m", "2h 5m", "1d 3h" from a minute count. */
export const formatDuration = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "0m"
  const d = Math.floor(minutes / 1440)
  const h = Math.floor((minutes % 1440) / 60)
  const m = minutes % 60
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

// ---------------------------------------------------------------------------
// Reservation migration counts
// ---------------------------------------------------------------------------
//
// REMOVED 2026-07-28: `formatReservationCell()` and the
// `reservationsMigrated/Failed/Remaining` row fields it read. Those were the
// pre-split counters; nothing had written them for some time, so the column they
// fed rendered "-" on every row. The real numbers are the split ACTIVE / HISTORY
// streams below, which the accumulator and the flip both maintain.

/**
 * Total reservations that did not make it — OUR processing failures (both streams)
 * plus the ones the PARTNER reported in a payload's failure_list. `partnerFailed`
 * is tracked separately on the record because it is excluded from the stream
 * completion formula, but for "did anything fail here?" all three count.
 */
export const reservationFailureCount = (row: Sstv2MigrationRow): number =>
  (row.activeFailed ?? 0) + (row.historyFailed ?? 0) + (row.partnerFailed ?? 0)

export const hasReservationFailures = (row: Sstv2MigrationRow): boolean =>
  reservationFailureCount(row) > 0

// ---------------------------------------------------------------------------
// Migration JOB progress (DEV-193, scale redesign §4.3/§6)
// ---------------------------------------------------------------------------

/**
 * i18n key for OUR processingState label (distinct from the partner phase).
 *
 * `MATERIALIZING_ACTIVE` keeps its name because it is a BACKEND ENUM VALUE
 * (`Sstv2MigrationProcessingStateList`) that the redesign did not rename — so the
 * key stays aligned with the enum it maps, and the *label* carries the correct
 * vocabulary ("flipping") instead. Renaming the key here would only decouple it
 * from the thing it names.
 *
 * ⚠ This returns a key for an ABSENT state and for an UNRECOGNISED one alike, and
 * the caller cannot tell them apart. That mattered: until the vocabulary table
 * existed, both landed on `migrationProcessingUnknown`, whose English reads "Not
 * started" — so the first state the backend shipped that this build did not know
 * would have rendered as an idle attempt. Use `describeProcessingState` +
 * `Sstv2TermChip` instead, which distinguishes the two and prints the raw value.
 */
export const processingStateLabelKey = (
  state?: Sstv2MigrationProcessingState,
): string => describeProcessingState(state).labelKey

/** MUI Chip color for a processingState. Rollback states are red; no yellow. */
export const processingStateChipColor = (
  state?: Sstv2MigrationProcessingState,
): "default" | "info" | "primary" | "success" | "error" => {
  switch (state) {
    case "ROLLBACK_STARTED":
    case "ROLLED_BACK":
      return "error"
    case "COMPLETE":
      return "success"
    case "OPERATIONAL":
    case "DRAINING_HISTORY":
      return "primary"
    case "MATERIALIZING_ACTIVE":
      return "info"
    default:
      return "default"
  }
}

/**
 * Progress of one reservation stream (ACTIVE or HISTORICAL). "Done" = flipped +
 * adopted + failed (all three are terminal for a reservation); the bar fills to
 * done/received and failures are called out separately. Pure + deterministic for
 * testing.
 *
 * ⚠ REVISED 2026-07-31 — D37 supersedes D5. Both streams reach "done" by the SAME
 * mechanism: the durable flip walks that stream's mapping partition. This comment
 * previously said HISTORY was done at INGESTION because history was never flipped;
 * that is no longer true, and the ingestion-time `historyProcessed` auto-credit was
 * removed in the same commit (`d9047759b`) — leaving it would have fired `COMPLETE`
 * before the history walk ran.
 *
 * They are still counted separately, for a different reason: the streams are walked
 * sequentially (active first) and folded independently, each behind its own arrival
 * gate, so they legitimately reach 100% at different times. A full active bar beside
 * a partial history bar is a normal mid-migration state, not a fault.
 */
export type StreamProgress = {
  received: number
  /** Flipped in place: the existing V1 row rewritten to V2 identity. */
  processed: number
  failed: number
  /** Adopted: a new Thing from the partner snapshot; counts as done, not failed. */
  adopted: number
  /**
   * processed + adopted — reservations that are LIVE ON V2 now, by either route.
   *
   * Renamed from `materialized` 2026-07-29: nothing is materialized any more.
   * The overwhelming majority of this number is rows flipped in place, and calling
   * that "materialized" invited operators to look for new reservations that do not
   * and should not exist.
   */
  liveOnV2: number
  done: number
  /** done / received, clamped to 0..100. 0 when nothing received yet. */
  pct: number
  /** failed / received, clamped to 0..100. */
  failPct: number
  hasFailures: boolean
  /** true once nothing has been received for this stream (bar shows empty). */
  empty: boolean
  /**
   * FOLD PROVENANCE — how many mappings a COMPLETED walk established this stream's
   * numerators against. `undefined` on any attempt that ran before the stamp existed.
   *
   * Written only by the flip's fold, in the same conditional update as the numerators
   * it certifies, so the stamp and the numbers can never disagree.
   */
  foldedCount?: number
  /**
   * Did a completed walk certify these numbers?
   *
   * ## Why the bar being full is not the same question
   *
   * This is the distinction the whole recovery arm rests on, and until now the UI could
   * not show it: a stream can be at 100% and unproven. Migration `ace1c2c7…` reads
   * `historyProcessed === historyReceived` because the pre-2026-07-31 ingest path
   * credited `historyProcessed := historyReceived` at write-down — nothing ever walked
   * its history partition. Counter arithmetic cannot tell that record from a finished
   * one. The absence of a stamp can.
   *
   * The test is `foldedCount >= received`, and it is deliberately self-invalidating:
   * `received` is a monotone projection of the same partition, so a mapping arriving
   * AFTER the fold raises it above the stamp and the stream stops being proven with no
   * invalidation hook anywhere in the ingest path.
   *
   * `false` is therefore NOT an error — it is the normal state of a stream that has not
   * been walked yet, and of every attempt older than the stamp. It is only alarming
   * beside a `COMPLETE` processingState.
   */
  proven: boolean
}

export const computeStreamProgress = (
  received?: number,
  processed?: number,
  failed?: number,
  adopted?: number,
  foldedCount?: number,
): StreamProgress => {
  const r = Math.max(0, received ?? 0)
  const p = Math.max(0, processed ?? 0)
  const f = Math.max(0, failed ?? 0)
  const a = Math.max(0, adopted ?? 0)
  // "done" mirrors the backend completion formula: processed + adopted + failed.
  const done = p + a + f
  const pct = r > 0 ? Math.min(100, Math.max(0, (done / r) * 100)) : 0
  const failPct = r > 0 ? Math.min(100, Math.max(0, (f / r) * 100)) : 0
  return {
    received: r,
    processed: p,
    failed: f,
    adopted: a,
    liveOnV2: p + a,
    done,
    pct,
    failPct,
    hasFailures: f > 0,
    empty: r === 0,
    foldedCount,
    // Mirrors `streamFoldProven` in `sstv2MigrationCompletion.ts`. An ABSENT stamp is
    // never proven — including at `received === 0`, where the backend requires the
    // recovery arm's explicit empty-partition seal to write a 0 rather than inferring
    // one. Inferring it here would show "proven" for every pre-stamp attempt, which is
    // exactly the false reassurance the stamp exists to remove.
    proven: foldedCount !== undefined && foldedCount >= r,
  }
}

/** Format a stream progress cell, e.g. "48/50 · 2 failed". */
export const formatStreamProgress = (s: StreamProgress): string => {
  // Numerator is LIVE ON V2 (flipped + adopted): reservations answering to V2 now.
  // Adopted rows are named separately because a high count means SL2 was not the
  // origin of that locker's reservations — every one of those is a NEW Thing,
  // unlike a flip, which rewrote a row that already existed.
  const base = `${s.liveOnV2}/${s.received}`
  const parts = [base]
  if (s.adopted > 0) parts.push(`${s.adopted} adopted`)
  if (s.hasFailures) parts.push(`${s.failed} failed`)
  return parts.join(" · ")
}

/**
 * One-line reservation progress for the grid, e.g. "active 4/4 · history 300/396".
 * Reads the split counters the pipeline actually maintains. "—" until the first
 * batch lands, so an untouched row is visibly untouched rather than fake-zero.
 */
export const formatStreamsCell = (row: Sstv2MigrationRow): string => {
  // The grid cell deliberately does NOT pass the fold stamps. Provenance is a
  // yes/no that needs its own explanation to mean anything, and a bare glyph in a
  // dense grid column would be read as a failure. It belongs in the drawer, beside
  // the gloss — see `Sstv2StreamProvenance`.
  const active = computeStreamProgress(
    row.activeReceived,
    row.activeProcessed,
    row.activeFailed,
    row.activeAdopted,
  )
  const history = computeStreamProgress(
    row.historyReceived,
    row.historyProcessed,
    row.historyFailed,
    row.historyAdopted,
  )
  if (active.empty && history.empty) return "—"
  const parts: string[] = []
  if (!active.empty) parts.push(`A ${active.done}/${active.received}`)
  if (!history.empty) parts.push(`H ${history.done}/${history.received}`)
  return parts.join(" · ")
}
